<template>
    <div>
        <v-toolbar flat class="products-toolbar">
            <!-- textual filter -->
            <v-text-field v-model="textualFilter"
                          :label="$t('products.textual_filter')"
                          class="mr-2"
                          clearable
                          hide-details
                          outlined
                          dense
            />

            <!-- brand filter -->
            <v-select v-model="brandFilter"
                      :label="$t('products.brand')"
                      :items="brands"
                      item-value="id"
                      item-text="name"
                      class="mr-2"
                      clearable
                      hide-details
                      outlined
                      dense
                      :disabled="isBusyBrands"
            ></v-select>

            <!-- in stock only filter -->
            <v-select v-model="inStockFilter"
                      :label="$t('products.availability')"
                      :items="inStockFilterItems"
                      class="mr-2"
                      clearable
                      hide-details
                      outlined
                      dense
            ></v-select>

            <!-- table/card view toggle -->
            <v-btn-toggle v-model="viewMode" mandatory color="primary" dense group>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn value="card" text v-on="on">
                            <v-icon>mdi-view-grid</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t('tooltip.product.card_view') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn value="table" text v-on="on">
                            <v-icon>mdi-format-list-bulleted</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t('tooltip.product.table_view') }}</span>
                </v-tooltip>
            </v-btn-toggle>
        </v-toolbar>

        <!-- products (cards) -->
        <v-row v-if="cardView">
            <v-col v-for="(product, index) in products" :key="index" cols="12" md="6" lg="4" xl="2">
                <v-card class="mx-auto product-card" height="100%">
                    <v-img class="white--text align-end cursor-pointer"
                           :src="productImage(product)"
                           aspect-ratio="1.4"
                           contain
                           @click="onProductDetail(product)"
                    ></v-img>
                    <v-card-title class="title cursor-pointer mb-2" @click="onProductDetail(product)">
                        {{ product.name }}
                    </v-card-title>
                    <v-card-subtitle>
                        <template v-if="showProductBrand">
                            <b>{{ $t('products.brand') }}</b> {{ product.brand }}<br>
                        </template>
                        <template v-if="showProductModel">
                            <b>{{ $t('products.model') }}</b> {{ product.model }}<br>
                        </template>
                        <template v-if="showProductType">
                            <b>{{ $t('products.product_type') }}</b>
                            {{ product.product_type ? product.product_type : '-' }}<br>
                        </template>
                        <template v-if="showProductInStock">
                            <b>{{ $t('products.in_stock_alt') }}</b> {{ product.in_stock }} {{ $t('products.pieces') }}
                        </template>
                    </v-card-subtitle>
                    <v-card-actions class="product-actions">
                        <add-to-cart-form-component :warehouse-id="warehouseId" :product="product"/>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <!-- products (table) -->
        <v-row v-if="tableView">
            <v-col cols="12">
                <v-data-table class="elevation-3"
                              :headers="headers"
                              :items="products"
                              hide-default-footer
                              :items-per-page="products.length"
                              :loading="false"
                >
                    <template v-slot:item.image_url="{ item }">
                        <v-img width="30"
                               height="30"
                               :src="productImage(item)"
                               contain
                        />
                    </template>
                    <template v-slot:item.detail="{ item }">
                        <v-btn icon @click="onProductDetail(item)">
                            <v-icon>mdi-magnify</v-icon>
                        </v-btn>
                    </template>
                    <template v-slot:item.action="{ item }">
                        <add-to-cart-form-component :warehouse-id="warehouseId" :product="item"/>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <!-- messages -->
        <v-container class="my-10">
            <!-- all products fetched message -->
            <p class="subtitle-1 grey--text text-center" v-if="allFetched && !isBusy">
                {{ $t('products.all_fetched') }}
            </p>
            <!-- loading -->
            <div class="text-center" v-if="isBusy">
                <v-progress-circular :size="40" :width="5" color="primary" indeterminate></v-progress-circular>
                <p class="mt-3 grey--text">{{ $t('common.loading_table_data') }}</p>
            </div>
        </v-container>

        <!-- product detail dialog -->
        <v-dialog v-model="isBusyProductDetail" persistent max-width="280">
            <v-card>
                <v-card-text class="py-6 text-center">
                    <v-progress-circular :size="40" :width="5" color="primary" indeterminate></v-progress-circular>
                    <p class="mt-3 mb-0 grey--text">{{ $t('common.loading_table_data') }}</p>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="productDetailDialog" max-width="1000">
            <v-card>
                <v-card-title class="headline">
                    {{ product ? product.name : '' }}
                    <v-spacer/>
                    <v-btn @click="productDetailDialog = false" icon>
                        <v-icon>mdi-window-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <product-detail-component :warehouse-id="warehouseId" :product="product"/>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { API } from "@/api/api";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import { EventBus } from "@/eventBus";
import AddToCartFormComponent from "@/components/user/products/AddToCartFormComponent";
import ProductDetailComponent from "@/components/user/products/ProductDetailComponent";
import ShoppingCartMixin from "@/mixins/ShoppingCartMixin";
import { debounce } from "debounce";
import ProductMixin from '@/mixins/ProductMixin'

export default {
    name: 'ProductsComponent',
    components: { ProductDetailComponent, AddToCartFormComponent },
    mixins: [SnackbarMixin, ShoppingCartMixin, ProductMixin],
    data: () => ({
        // loading
        isBusyProducts: false,
        isBusyBrands: false,
        isBusyProductDetail: false,
        // data
        products: [],
        brands: [],
        // filters
        textualFilter: null,
        inStockFilter: null,
        brandFilter: null,
        productTypesFilter: {},
        productTypesAttributesFilter: {},
        // pagination
        page: 1,
        perPage: 12,
        // infinite scroll
        bottom: false,
        allFetched: false,
        // table/card view toggle
        viewMode: 'card',
        // dialogs
        productDetailDialog: false,
        product: null,
        // request queue
        requestEpoch: 0,
        productDetailRequestEpoch: 0,
        isProductTypeFilterInitialized: false
    }),
    computed: {
        isBusy () {
            return this.isBusyProducts || this.isBusyBrands;
        },
        cardView () {
            return this.viewMode === 'card';
        },
        tableView () {
            return this.viewMode === 'table';
        },
        warehouseId () {
            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            return this.$store.getters['warehouse/getWarehouseId'](userId);
        },
        inStockFilterItems () {
            return [
                {
                    text: this.$t('products.filter.in_stock'),
                    value: 1 // true
                },
                {
                    text: this.$t('products.filter.not_in_stock'),
                    value: 0 // false
                }
            ]
        },
        headers () {
            return [
                {
                    text: this.$t('products.thumbnail'),
                    value: 'image_url',
                    sortable: false
                },
                {
                    text: this.$t('products.name'),
                    value: 'name',
                    sortable: false
                },
                {
                    text: this.$t('products.model'),
                    value: 'model',
                    sortable: false
                },
                {
                    text: this.$t('products.brand'),
                    value: 'brand',
                    sortable: false
                },
                {
                    text: this.$t('products.in_stock_alt_2'),
                    value: 'in_stock',
                    align: 'end',
                    sortable: false
                },
                {
                    text: this.$t('products.detail'),
                    value: 'detail',
                    align: 'center',
                    width: 0,
                    sortable: false
                },
                {
                    text: this.$t('products.add_to_cart'),
                    value: 'action',
                    align: 'center',
                    width: 200,
                    sortable: false
                }
            ];
        }
    },
    watch: {
        bottom (bottom) {
            if (!this.allFetched && bottom) {
                this.fetchProducts();
            }
        },
        textualFilter () {
            this.resetInfiniteScroll();
            this.fetchProducts();

            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            let warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
            if (warehouseId === null) {
                this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
                return;
            }

            this.$store.commit('filter/setTextualFilter', {
                userId: userId,
                warehouseId: warehouseId,
                textualFilter: this.textualFilter
            });
        },
        brandFilter () {
            if (this.brandFilter === undefined) {
                this.brandFilter = null;
            }

            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            let warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
            if (warehouseId === null) {
                this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
                return;
            }

            this.$store.commit('filter/setBrandFilter', {
                userId: userId,
                warehouseId: warehouseId,
                brandFilter: this.brandFilter
            });

            this.resetInfiniteScroll();
            this.fetchProducts();
        },
        inStockFilter () {
            if (this.inStockFilter === undefined) {
                this.inStockFilter = null;
            }

            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            let warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
            if (warehouseId === null) {
                this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
                return;
            }

            this.$store.commit('filter/setInStockFilter', {
                userId: userId,
                warehouseId: warehouseId,
                inStockFilter: this.inStockFilter
            });

            this.resetInfiniteScroll();
            this.fetchProducts();
        },
        viewMode () {
            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            let warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
            if (warehouseId === null) {
                this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
                return;
            }

            this.$store.commit('filter/setViewMode', {
                userId: userId,
                warehouseId: warehouseId,
                viewMode: this.viewMode
            });
        }
    },
    methods: {
        fetchProducts () {
            if (!this.isProductTypeFilterInitialized) {
                return;
            }

            if (this.allFetched) {
                return;
            }

            if (this.warehouseId === null) {
                this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
                return;
            }

            const epoch = this.requestEpoch;

            let url = '/api/warehouses/'
                + encodeURI(this.warehouseId)
                + '/products?page='
                + encodeURI(this.page)
                + '&perPage='
                + encodeURI(this.perPage);

            const localizationLanguage = process.env.VUE_APP_LOCALIZATION_LANUGUAGE;
            if (localizationLanguage) {
                url += '&localizationLanguage=' + encodeURI(localizationLanguage);
            }

            if (this.textualFilter) {
                url += '&filter=' + encodeURI(this.textualFilter);
            }

            if (this.inStockFilter !== null) {
                url += '&inStock=' + encodeURI(this.inStockFilter);
            }

            if (this.brandFilter !== null) {
                url += '&brandId=' + encodeURI(this.brandFilter);
            }

            if (Object.values(this.productTypesFilter).length > 0) {
                Object.entries(this.productTypesFilter).forEach(filter => {
                    const [productTypeId, value] = filter;
                    if (value === true) {
                        url += ('&productTypeIds[]=' + productTypeId);
                    }
                });
            }

            if (Object.values(this.productTypesAttributesFilter).length > 0) {
                Object.entries(this.productTypesAttributesFilter).forEach(filter => {
                    const [key, value] = filter;
                    if (![null, undefined, ''].includes(value)) {
                        url += ('&productTypeAttributes[' + encodeURI(key) + ']=' + encodeURI(value));
                    }
                });
            }

            this.isBusyProducts = true;
            API.get(url)
                .then(response => {
                    if (epoch !== this.requestEpoch) {
                        return;
                    }

                    if (response.data.length === 0) {
                        this.allFetched = true;
                    } else {
                        this.page += 1;
                    }

                    response.data.forEach(newProduct => {
                        const exists = this.products.some(existingProduct => existingProduct.external_id === newProduct.external_id);
                        if (!exists) this.products.push(newProduct);
                    });

                    if (this.bottomVisible()) {
                        this.fetchProducts();
                    }
                })
                .catch(error => {
                    if (epoch !== this.requestEpoch) {
                        return;
                    }

                    this.snackbarError(error);
                })
                .finally(() => {
                    if (epoch !== this.requestEpoch) {
                        return;
                    }

                    this.isBusyProducts = false;
                });
        },
        fetchBrands () {
            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            this.isBusyBrands = true;
            API.get('/api/brands')
                .then(response => {
                    this.brands = response.data.filter(brand => brand.warehouse_ids.includes(this.warehouseId));
                    this.brands.sort((a, b) => {
                        return a.name.localeCompare(b.name);
                    });
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyBrands = false;
                });
        },
        bottomVisible () {
            const offset = 250;
            const scrollY = window.scrollY;
            const visible = document.documentElement.clientHeight;
            const pageHeight = document.documentElement.scrollHeight;
            return visible + scrollY >= pageHeight - offset || pageHeight - offset < visible;
        },
        productImage (product) {
            return product.image_url;
        },
        resetInfiniteScroll () {
            this.requestEpoch += 1;
            this.products = [];
            this.page = 1;
            this.bottom = false;
            this.allFetched = false;
        },
        onProductDetail (product) {
            if (this.warehouseId === null) {
                return;
            }

            const epoch = ++this.productDetailRequestEpoch;
            let url = '/api/warehouses/' + encodeURI(this.warehouseId) + '/products/' + encodeURI(product.external_id);
            const localizationLanguage = process.env.VUE_APP_LOCALIZATION_LANUGUAGE;
            if (localizationLanguage) {
                url += '?localizationLanguage=' + encodeURI(localizationLanguage);
            }

            this.isBusyProductDetail = true;
            API.get(url)
                .then(response => {
                    if (epoch !== this.productDetailRequestEpoch) {
                        return;
                    }

                    this.product = response.data;
                    this.productDetailDialog = true;
                })
                .catch(error => {
                    if (epoch === this.productDetailRequestEpoch) {
                        this.snackbarError(error);
                    }
                })
                .finally(() => {
                    if (epoch === this.productDetailRequestEpoch) {
                        this.isBusyProductDetail = false;
                    }
                });
        }
    },
    created () {
        this.page = 1;
        this.perPage = 12;

        window.addEventListener('scroll', () => {
            this.bottom = this.bottomVisible();
        });

        // debounce "fetchProducts" method to prevent calling method with each new letter typed into filter (https://www.npmjs.com/package/debounce)
        this.fetchProducts = debounce(this.fetchProducts, 250);

        this.fetchBrands();

        EventBus.$on('close-product-dialogs', () => {
            this.productDetailDialog = false;
        });

        let userId = this.$store.getters['identity/getUserId'];
        if (userId === null) {
            this.snackbarError(this.$t('snackbar.identity_check_failed'));
            return;
        }

        let warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
        if (warehouseId === null) {
            this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
            return;
        }

        // set filters default values
        this.textualFilter = this.$store.getters['filter/getTextualFilter'](userId, warehouseId);
        this.brandFilter = this.$store.getters['filter/getBrandFilter'](userId, warehouseId);
        this.inStockFilter = this.$store.getters['filter/getInStockFilter'](userId, warehouseId);
        this.viewMode = this.$store.getters['filter/getViewMode'](userId, warehouseId);

        // product type/product type attributes filters
        EventBus.$on('product-types-initialized', (productTypesFilter) => {
            this.productTypesFilter = { ...productTypesFilter };
            this.isProductTypeFilterInitialized = true;
            this.resetInfiniteScroll();
            this.fetchProducts();
        });
        EventBus.$on('product-type', (productType, value) => {
            this.$set(this.productTypesFilter, productType.id, value);
            this.resetInfiniteScroll();
            this.fetchProducts();
        });
        EventBus.$on('product-type-attribute', (productTypeAttribute, value) => {
            this.$set(this.productTypesAttributesFilter, productTypeAttribute.key, value);
            this.resetInfiniteScroll();
            this.fetchProducts();
        });
    }
}
</script>

<style lang="scss">
.products-toolbar {
  .v-toolbar__content {
    padding-left: 0;
    padding-right: 0;
  }
}

.product-card {
  position: relative;
  padding-bottom: 50px;

  .v-card__text, .v-card__title {
    word-break: normal;
  }

  .product-actions {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }

  .cursor-pointer {
    cursor: pointer;
  }
}
</style>
